import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { getPacientesControllerListarQueryKey } from '@/api/generated/pacientes/pacientes';
import {
  getTagsDePacienteControllerListarQueryKey,
  useTagsDePacienteControllerAtualizar,
  useTagsDePacienteControllerCriar,
  useTagsDePacienteControllerExcluir,
  useTagsDePacienteControllerListar,
} from '@/api/generated/tags-de-paciente/tags-de-paciente';
import { ApiError } from '@/api/http-client';
import { COR_PADRAO } from '@/shared/lib/cores';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { SeletorDeCor } from '@/shared/ui/seletor-de-cor';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * Gestão das tags de paciente — criar, renomear/recolorir, excluir. Fora do
 * card de propósito (ver `VincularTagsDialogo`, que fica só com o
 * marcar/desmarcar de UM paciente): cadastrar uma tag não é sobre nenhum
 * animal específico, é sobre o ambiente inteiro — igual `SeletorDeGrupo` ×
 * `MontadorDeGrupo` na análise comparativa, "escolher" e "criar" são ações
 * diferentes.
 */
export function GerenciarTagsDialogo({ aberto, aoFechar }: Props) {
  const clienteDeQuery = useQueryClient();

  const [editando, setEditando] = useState<string | null>(null);
  const [nomeEdicao, setNomeEdicao] = useState('');
  const [corEdicao, setCorEdicao] = useState(COR_PADRAO);
  const [criandoNova, setCriandoNova] = useState(false);
  const [nomeNova, setNomeNova] = useState('');
  const [corNova, setCorNova] = useState(COR_PADRAO);

  const { data: pagina, isPending: carregando } = useTagsDePacienteControllerListar({
    porPagina: 100,
  });
  const tags = pagina?.itens ?? [];

  const criar = useTagsDePacienteControllerCriar();
  const atualizar = useTagsDePacienteControllerAtualizar();
  const excluir = useTagsDePacienteControllerExcluir();

  function invalidar() {
    void clienteDeQuery.invalidateQueries({ queryKey: getTagsDePacienteControllerListarQueryKey() });
    // Nome/cor mudam o chip do hover nos cards; excluir tira o badge inteiro
    // — os dois casos afetam o que já está desenhado na lista de pacientes.
    void clienteDeQuery.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
  }

  function fechar() {
    setEditando(null);
    setCriandoNova(false);
    aoFechar();
  }

  function abrirEdicao(id: string, nome: string, cor: string) {
    setCriandoNova(false);
    setEditando(id);
    setNomeEdicao(nome);
    setCorEdicao(cor);
  }

  async function salvarEdicao() {
    if (!editando || !nomeEdicao.trim()) return;
    try {
      await atualizar.mutateAsync({ id: editando, data: { nome: nomeEdicao.trim(), cor: corEdicao } });
      invalidar();
      setEditando(null);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não conseguimos salvar a tag.');
    }
  }

  async function excluirTag(id: string) {
    try {
      await excluir.mutateAsync({ id });
      invalidar();
      if (editando === id) setEditando(null);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não conseguimos excluir a tag.');
    }
  }

  async function criarNova() {
    if (!nomeNova.trim()) return;
    try {
      await criar.mutateAsync({ data: { nome: nomeNova.trim(), cor: corNova } });
      invalidar();
      setCriandoNova(false);
      setNomeNova('');
      setCorNova(COR_PADRAO);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não conseguimos criar a tag.');
    }
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(estado) => {
        if (!estado) fechar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tags de paciente</DialogTitle>
          <DialogDescription>
            Crie, renomeie ou recolora as tags do ambiente. Para marcar um paciente com uma tag, use
            o menu de 3 pontos no card dele.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-80 overflow-y-auto">
          {carregando && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Carregando tags...
            </p>
          )}

          {!carregando && tags.length === 0 && !criandoNova && (
            <p className="text-sm text-muted-foreground py-4">
              Nenhuma tag criada neste ambiente ainda.
            </p>
          )}

          {tags.map((tag) =>
            editando === tag.id ? (
              <div key={tag.id} className="space-y-2 rounded-md border border-brand/20 p-2">
                <Input
                  value={nomeEdicao}
                  onChange={(evento) => setNomeEdicao(evento.target.value)}
                  placeholder="Nome da tag"
                />
                <SeletorDeCor cor={corEdicao} aoMudar={setCorEdicao} />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditando(null)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    disabled={!nomeEdicao.trim() || atualizar.isPending}
                    onClick={() => void salvarEdicao()}
                  >
                    {atualizar.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={tag.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50"
              >
                <span
                  className="inline-block size-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.cor }}
                />
                <span className="flex-1 truncate text-sm text-foreground">{tag.nome}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {tag.totalPacientes === 1 ? '1 paciente' : `${tag.totalPacientes} pacientes`}
                </span>
                <button
                  type="button"
                  aria-label={`Editar tag ${tag.nome}`}
                  onClick={() => abrirEdicao(tag.id, tag.nome, tag.cor)}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Excluir tag ${tag.nome}`}
                  onClick={() => void excluirTag(tag.id)}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ),
          )}

          {criandoNova && (
            <div className="space-y-2 rounded-md border border-brand/20 p-2">
              <Input
                autoFocus
                value={nomeNova}
                onChange={(evento) => setNomeNova(evento.target.value)}
                placeholder="Ex.: Cachorro porte Grande"
              />
              <SeletorDeCor cor={corNova} aoMudar={setCorNova} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setCriandoNova(false)}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={!nomeNova.trim() || criar.isPending} onClick={() => void criarNova()}>
                  {criar.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                  Criar
                </Button>
              </div>
            </div>
          )}
        </div>

        {!criandoNova && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 self-start"
            onClick={() => {
              setEditando(null);
              setCriandoNova(true);
            }}
          >
            <Plus className="w-3.5 h-3.5" />
            Nova tag
          </Button>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={fechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
